<script setup lang="ts">
import { computed, useSlots } from 'vue';

interface AlternativeSuggestionProductCardLayoutProps {
  enableHoverColor: boolean;
  shouldHideProductLink: boolean;
}

const props = defineProps<AlternativeSuggestionProductCardLayoutProps>();

const slots = useSlots();
const hasHeader = computed(() => !!slots['header']);
</script>

<template>
  <div
    class="alternative-suggestion-card-layout"
    :class="{ experiment: props.shouldHideProductLink }"
  >
    <slot name="header" />
    <div
      class="product-content"
      :class="{
        hovering: props.enableHoverColor,
        'has-header': hasHeader,
      }"
    >
      <div class="image">
        <slot name="image" />
      </div>
      <div class="content" :class="{ 'has-header': hasHeader }">
        <slot name="content" />
        <div class="footer">
          <slot name="footer" />
        </div>
      </div>

      <slot name="tag" />
      <slot name="trash-icon" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.alternative-suggestion-card-layout {
  border-radius: var(--border-radius-8);
  border: 1px solid var(--color-neutral-30);
  overflow: hidden;

  &.experiment {
    .product-content {
      &.hovering {
        @include on-hover {
          background: var(--color-white);
          cursor: default;
        }
      }
    }
  }

  .product-content {
    width: 100%;
    background: var(--color-white);
    display: grid;
    grid-template-columns: 3fr 5fr;
    position: relative;
    padding: var(--spacing-16);

    &.hovering {
      @include on-hover {
        background: var(--color-neutral-30);
        transition: background var(--transition-short);
        cursor: pointer;
      }
    }

    .image {
      width: 100%;
      max-height: 200px;
      cursor: pointer;

      overflow: hidden;
      // NOTE: Next two rules are forcing image to not be highier
      // than .content column
      height: 0;
      min-height: 100%;

      @include for-more-or-equal-than-600-px {
        border-radius: var(--spacing-8);
      }
    }

    .content {
      display: flex;
      flex-direction: column;
    }
  }

  .product-content.has-header {
    border-top: 1px solid var(--color-neutral-30);
  }

  @include for-less-or-equal-than-599-px {
    .product-content {
      grid-template-rows: repeat(4, auto);
      // FYI: should be auto, but firefox mobile on android have issues with it
      grid-template-columns: 1fr;
      padding: 0;

      .image {
        grid-column: 1/2;
        width: 100%;
        height: auto;
        aspect-ratio: 16/9;
        overflow: hidden;
      }

      .content {
        grid-column: 1/2;
        border-bottom: unset;
        border-bottom-left-radius: unset;
      }

      .footer {
        padding-bottom: var(--spacing-16);
      }
    }
  }
}
</style>
